import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Users, CalendarDays, MapPin, BedDouble, Bath, MessageCircle } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { VACATION_WHATSAPP, waLinkTo } from "@/lib/brand";
import useSEO from "@/hooks/useSEO";

export default function Vacanze() {
  useSEO({ title: "Case Vacanza in Puglia · Affitti Brevi | Casamore Group", description: "Vieni in vacanza in Puglia con Casamore Group: trulli, attici vista mare e dimore nel centro storico per i tuoi affitti brevi. Scopri le strutture e prenota il tuo soggiorno.", canonical: window.location.origin + "/vacanze" });
  const [structures, setStructures] = useState([]);
  const [dest, setDest] = useState("");
  const [ospiti, setOspiti] = useState("");
  const [arrivo, setArrivo] = useState("");
  const [partenza, setPartenza] = useState("");

  useEffect(() => {
    api.get("/public/vacation-rentals").then((r) => setStructures(r.data)).catch(() => setStructures([]));
  }, []);

  const results = useMemo(() => {
    const d = dest.trim().toLowerCase();
    return structures.filter((s) => {
      if (d && ![s.nome, s.localita, s.zona].some((v) => (v || "").toLowerCase().includes(d))) return false;
      if (ospiti && Number(s.maxOspiti || 0) < Number(ospiti)) return false;
      return true;
    });
  }, [structures, dest, ospiti]);

  const waMessage = (s) => {
    let m = `Salve, sono interessato alla struttura "${s.nome}" (${s.localita}).`;
    if (ospiti) m += ` Persone: ${ospiti}.`;
    if (arrivo) m += ` Arrivo: ${arrivo}.`;
    if (partenza) m += ` Partenza: ${partenza}.`;
    m += " Vorrei maggiori informazioni e disponibilità.";
    return m;
  };

  return (
    <div className="pt-24 pb-24">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/34939793/pexels-photo-34939793.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-anthracite/55" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12 py-16 sm:py-24 text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-white/80">
              <span className="h-px w-6 bg-white/50" /> Affitti brevi
            </span>
            <h1 className="font-display font-semibold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-4">
              Vieni in vacanza <span className="italic">da noi</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-white/85">
              Le nostre strutture selezionate per il tuo soggiorno in Puglia. Scegli destinazione, ospiti e date: pensiamo a tutto noi.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Search bar */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 -mt-10 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="bg-white rounded-3xl border border-border-subtle shadow-[0_25px_60px_-30px_rgba(15,92,74,0.4)] p-4 sm:p-5 grid grid-cols-1 md:grid-cols-4 gap-3"
          data-testid="vacanze-search"
        >
          <label className="flex items-center gap-3 rounded-2xl bg-accent-subtle px-4 py-3">
            <MapPin className="h-5 w-5 text-brand shrink-0" />
            <span className="flex-1">
              <span className="block text-[11px] font-semibold text-anthracite/50">Destinazione</span>
              <input value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Paese o zona" className="w-full bg-transparent outline-none text-sm font-medium text-anthracite placeholder:text-anthracite/40" data-testid="vacanze-dest" />
            </span>
          </label>
          <label className="flex items-center gap-3 rounded-2xl bg-accent-subtle px-4 py-3">
            <Users className="h-5 w-5 text-brand shrink-0" />
            <span className="flex-1">
              <span className="block text-[11px] font-semibold text-anthracite/50">Persone</span>
              <select value={ospiti} onChange={(e) => setOspiti(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium text-anthracite" data-testid="vacanze-ospiti">
                <option value="">Qualsiasi</option>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}+ ospiti</option>)}
              </select>
            </span>
          </label>
          <label className="flex items-center gap-3 rounded-2xl bg-accent-subtle px-4 py-3">
            <CalendarDays className="h-5 w-5 text-brand shrink-0" />
            <span className="flex-1">
              <span className="block text-[11px] font-semibold text-anthracite/50">Arrivo</span>
              <input type="date" value={arrivo} onChange={(e) => setArrivo(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium text-anthracite" data-testid="vacanze-arrivo" />
            </span>
          </label>
          <label className="flex items-center gap-3 rounded-2xl bg-accent-subtle px-4 py-3">
            <CalendarDays className="h-5 w-5 text-brand shrink-0" />
            <span className="flex-1">
              <span className="block text-[11px] font-semibold text-anthracite/50">Partenza</span>
              <input type="date" value={partenza} min={arrivo || undefined} onChange={(e) => setPartenza(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium text-anthracite" data-testid="vacanze-partenza" />
            </span>
          </label>
        </motion.div>
      </div>

      {/* Results */}
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 mt-10">
        <div className="flex items-center gap-2 text-brand mb-6">
          <Search className="h-4 w-4" />
          <span className="text-sm font-semibold uppercase tracking-wide" data-testid="vacanze-count">{results.length} strutture disponibili</span>
        </div>

        {results.length === 0 ? (
          <div className="rounded-3xl border border-border-subtle bg-white p-12 text-center text-anthracite/60" data-testid="vacanze-empty">
            Nessuna struttura corrisponde alla ricerca. Prova a modificare i filtri.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.map((s, i) => (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-white rounded-3xl border border-border-subtle overflow-hidden shadow-[0_10px_40px_-24px_rgba(15,92,74,0.35)] flex flex-col"
                data-testid={`vacanze-card-${s.id}`}
              >
                <Link to={`/vacanze/${s.id}`} className="relative overflow-hidden h-56 block" data-testid={`vacanze-cardlink-${s.id}`}>
                  <img src={fileUrl(s.immagini?.[0])} alt={s.nome} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  {s.prezzoNotte ? <span className="absolute bottom-3 left-3 rounded-full bg-white/90 backdrop-blur text-brand text-sm font-bold px-3 py-1">€ {s.prezzoNotte}/notte</span> : null}
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <Link to={`/vacanze/${s.id}`} className="font-semibold text-anthracite text-lg leading-tight hover:text-brand transition-colors">{s.nome}</Link>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-anthracite/60"><MapPin className="h-3.5 w-3.5 text-brand" /> {s.localita}{s.zona ? ` — ${s.zona}` : ""}</p>
                  <div className="mt-3 flex items-center gap-4 text-sm text-anthracite/70">
                    {s.maxOspiti ? <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-brand" /> {s.maxOspiti}</span> : null}
                    {s.camere ? <span className="inline-flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-brand" /> {s.camere}</span> : null}
                    {s.bagni ? <span className="inline-flex items-center gap-1.5"><Bath className="h-4 w-4 text-brand" /> {s.bagni}</span> : null}
                  </div>
                  <p className="mt-3 text-sm text-anthracite/60 line-clamp-3 flex-1">{s.descrizione}</p>
                  <a
                    href={waLinkTo(VACATION_WHATSAPP, waMessage(s))}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold py-3 transition-all hover:bg-brand-hover"
                    data-testid={`vacanze-wa-${s.id}`}
                  >
                    <MessageCircle className="h-4 w-4" /> Contattaci su WhatsApp
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
